import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, Info, Zap, ShieldCheck, DollarSign, Users, Briefcase, GraduationCap, TrendingUp, Search } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Painel IPTV para Revendedores Iniciantes", href: "/blog/painel-iptv-revendedores-iniciantes" }
  ];

  const sections = [
    {
      title: "O Primeiro Passo Rumo à Independência Financeira",
      content: (
        <div className="prose prose-invert max-w-none space-y-6 text-lg text-muted-foreground">
          <p>
            Entrar no mercado de IPTV em 2026 pode parecer intimidador, mas para o <strong>revendedor iniciante</strong>, este é o momento de maior oportunidade. Com a explosão do consumo de streaming e o aumento dos preços dos serviços tradicionais, o público busca alternativas de alta qualidade com preço justo. Começar do jeito certo é o que define se você terá apenas uma "renda extra" ou um negócio de 5 dígitos mensais.
          </p>
          <p>
            Muitos iniciantes cometem o erro de focar apenas no menor preço de crédito, ignorando a estabilidade e o suporte. Na P2 PRO, desenvolvemos um modelo <strong>pós-pago</strong> pensado especificamente para quem está começando: investimento zero em estoque de créditos e foco total em trazer clientes e lucrar desde o primeiro dia.
          </p>
          <div className="bg-electric/10 border-l-4 border-electric p-8 my-10 rounded-r-3xl relative overflow-hidden">
            <h4 className="text-white font-black mb-2 uppercase italic text-xl">Mantra do Iniciante</h4>
            <p className="italic text-white/90">
              "Não tente ser o mais barato, tente ser o mais estável. Um cliente que paga barato mas não consegue assistir o jogo no domingo vai te dar mais dor de cabeça do que lucro."
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Por Que o Modelo Pós-Pago é Ideal para Você?",
      type: "grid" as const,
      content: (
        <>
          {[
            { t: "Risco Financeiro Zero", d: "Não gaste milhares de reais comprando créditos adiantados. Você só paga pelo que seus clientes usarem.", i: <DollarSign /> },
            { t: "Foco nas Vendas", d: "Sem a preocupação de 'recarregar o painel', seu tempo fica livre para marketing e atendimento de qualidade.", i: <TrendingUp /> },
            { t: "Escala Sob Demanda", d: "Se você colocar 1 ou 100 clientes hoje, o painel está pronto. O sistema cresce junto com a sua ambição.", i: <Users /> },
            { t: "Suporte Master", d: "Iniciantes na P2 PRO têm acesso a treinamentos e suporte técnico para não ficarem na mão com dúvidas básicas.", i: <GraduationCap /> },
            { t: "Painel Intuitivo", d: "Interface simplificada que qualquer pessoa com um celular consegue operar com maestria em poucos minutos.", i: <Briefcase /> },
            { t: "Sinal de Elite", d: "Entregue para seus primeiros clientes a mesma qualidade que os maiores revendedores do Brasil entregam.", i: <Zap /> }
          ].map((item, i) => (
            <div key={i} className="p-8 rounded-[2.5rem] bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all group">
              <div className="w-14 h-14 rounded-2xl bg-electric/10 text-electric flex items-center justify-center mb-6">
                {item.i}
              </div>
              <h3 className="text-2xl font-black tracking-tighter mb-4 italic uppercase">{item.t}</h3>
              <p className="text-muted-foreground leading-relaxed">{item.d}</p>
            </div>
          ))}
        </>
      )
    },
    {
      title: "Roteiro do Sucesso para Sua Primeira Semana",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-lg text-muted-foreground">
            Siga estes 3 passos fundamentais para garantir que sua jornada comece com lucro e organização.
          </p>
          <div className="p-10 rounded-[3rem] bg-white/[0.02] border border-white/5 my-12 relative overflow-hidden">
             <div className="absolute top-0 right-0 w-64 h-64 bg-electric/5 blur-[80px] rounded-full" />
             <h4 className="text-2xl font-black mb-8 italic uppercase tracking-tighter text-white">Checklist do Revendedor Master</h4>
             <ul className="space-y-6">
               <li className="flex gap-6 items-start">
                 <div className="bg-electric text-black font-black p-2 rounded-xl text-xs shrink-0 shadow-glow italic">01</div>
                 <div>
                   <p className="text-white font-bold text-lg mb-1">Teste sua Própria Lista</p>
                   <p className="text-sm text-muted-foreground">Instale os aplicativos P2 PRO em seus dispositivos. Você precisa conhecer o produto que vende para passar confiança.</p>
                 </div>
               </li>
               <li className="flex gap-6 items-start">
                 <div className="bg-electric text-black font-black p-2 rounded-xl text-xs shrink-0 italic">02</div>
                 <div>
                   <p className="text-white font-bold text-lg mb-1">Crie seu Script de Vendas</p>
                   <p className="text-sm text-muted-foreground">Defina como você vai explicar o serviço. Foque na estabilidade, na quantidade de conteúdos e no seu suporte personalizado.</p>
                 </div>
               </li>
               <li className="flex gap-6 items-start">
                 <div className="bg-electric text-black font-black p-2 rounded-xl text-xs shrink-0 italic">03</div>
                 <div>
                   <p className="text-white font-bold text-lg mb-1">Ofereça Testes Estratégicos</p>
                   <p className="text-sm text-muted-foreground">Use a função de teste automático para deixar o cliente experimentar a qualidade. 80% dos testes viram assinaturas na P2 PRO.</p>
                 </div>
               </li>
             </ul>
          </div>
        </div>
      )
    }
  ];

  return (
    <SEOPage
      category="Dicas Painel IPTV POS PAGO"
      title="Painel IPTV para Revendedores Iniciantes: Guia do Zero ao Lucro"
      subtitle="Tudo o que você precisa saber para começar sua revenda com investimento mínimo, suporte de elite e escala real em 2026."
      heroImage="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200"
      introParagraphs={[
        "Você quer começar no mercado de IPTV mas não sabe por onde começar? Já ouviu falar de painéis que travam ou de investimentos altos em créditos que ficam parados? O guia de hoje foi feito para você, que deseja entrar neste mercado de forma profissional e lucrativa desde o primeiro dia.",
        "Muitos revendedores iniciantes desistem nos primeiros meses por falta de orientação técnica ou por escolherem servidores instáveis. Na P2 PRO, nossa missão é fornecer não apenas o sinal, mas todo o ecossistema necessário para você crescer. Do painel simplificado ao suporte 24h, estamos aqui para sustentar seu progresso.",
        "Aprenda como utilizar o modelo pós-pago a seu favor, como abordar seus primeiros clientes e por que a estabilidade do sinal é o seu maior vendedor silencioso. Vamos transformar sua vontade de empreender em faturamento real."
      ]}
      benefits={[
        { title: "Investimento Mínimo", description: "Comece seu negócio sem precisar desembolsar grandes quantias em créditos." },
        { title: "Suporte Especializado", description: "Tenha uma equipe de masters te ajudando a resolver dúvidas técnicas iniciais." },
        { title: "Sinal Premium", description: "Diferencie-se da concorrência com um servidor que não trava em horários de pico." },
        { title: "Liberdade de Horário", description: "Gerencie sua revenda de qualquer lugar, apenas com o seu smartphone." }
      ]}
      howItWorks={[
        { title: "Cadastro", description: "Ative seu painel master na P2 PRO e receba suas credenciais de acesso." },
        { title: "Treinamento", description: "Assista aos nossos tutoriais rápidos sobre como gerar logins e testes." },
        { title: "Divulgação", description: "Utilize nossos materiais de marketing prontos para atrair seus primeiros clientes." },
        { title: "Faturamento", description: "Receba dos seus clientes e acerte apenas o consumo no final do mês." }
      ]}
      diferenciais={[
        { title: "Zero Estoque", description: "O modelo pós-pago elimina a necessidade de comprar créditos antes de vender." },
        { title: "Conversão Alta", description: "Aplicativos exclusivos que encantam o cliente logo no primeiro teste." },
        { title: "Escalabilidade", description: "Fique pronto para gerenciar de 10 a 1000 clientes com o mesmo painel." }
      ]}
      faq={[
        { question: "Preciso de um computador para começar?", answer: "Não. O painel da P2 PRO é totalmente responsivo e pode ser operado 100% pelo celular com total eficiência." },
        { question: "Como recebo dos meus clientes?", answer: "Você é o dono do seu negócio. Pode receber via PIX, Cartão ou Boleto diretamente na sua conta e só paga a P2 PRO pelo consumo realizado." },
        { question: "O sinal é garantido?", answer: "Sim, utilizamos infraestrutura de ponta com redundância global para garantir 99.9% de uptime, mesmo nos maiores eventos esportivos." }
      ]}
      breadcrumbs={breadcrumbs}
      sections={sections}
      ctaTitle="🚀 Pronto para Mudar sua Vida Financeira?"
      ctaDescription="Junte-se à elite dos revendedores P2 PRO e comece seu negócio do jeito certo hoje mesmo."
      ctaButtonText="Quero Começar Agora"
    />
  );
};

export const Route = createFileRoute("/blog/painel-iptv-revendedores-iniciantes")({
  head: () => ({
    meta: [
      { title: "Painel IPTV para Revendedores Iniciantes | P2 PRO" },
      { name: "description", content: "Guia completo para iniciantes na revenda de IPTV. Aprenda como começar do zero, lucrar no modelo pós-pago e escalar seu negócio com o suporte da P2 PRO." },
      { name: "keywords", content: "revenda iptv iniciante, começar revenda iptv, painel iptv pos pago, ganhar dinheiro com iptv" }
    ],
  }),
  component: ArticlePage,
});
